import { isEnabled } from '../../../../shared/featureFlags.js';

export const editorWorkspaceEnabled = () => import.meta.env.VITE_MAGNET_EDITOR_WORKSPACE === 'true'
  || isEnabled('FF_M5M_MAGNET_EDITOR_WORKSPACE');
export const EDITOR_SECTIONS = Object.freeze({
  conversation: 'flow', design: 'appearance', delivery: 'delivery', website: 'basics', review: 'review',
  welcome: 'welcome', confirmation: 'finish', experiment: 'ab',
});
export const editorPath = (guid, section = 'conversation') => `/magnets/${encodeURIComponent(guid)}/edit/${section}`;
export function parseEditorPath(path) {
  const match = String(path || '').replace(/^#/, '').match(/^\/?magnets\/([\w-]+)\/edit(?:\/(conversation|design|delivery|website|review|welcome|confirmation|experiment))?\/?$/);
  return match ? { guid: match[1], section: match[2] || 'conversation' } : null;
}

let active = null;
let suspended = null;
export const getSuspendedEditor = () => suspended;
// Keep the exact controls, flow models and staged logo in this tab. Nothing is
// persisted to browser storage or sent to the server as part of recovery.
export function suspendEditorSession() {
  if (suspended) return true;
  if (!active?.actor || !active.element.isConnected || !active.controller.suspend?.()) return false;
  suspended = active;
  suspended.resumeRequested = false;
  active = null;
  suspended.element.inert = true;
  suspended.element.remove();
  rememberEditorReturn(suspended.path);
  return true;
}
export function discardSuspendedEditor() {
  const previous = suspended;
  suspended = null;
  previous?.controller.destroy();
  if (previous) clearEditorReturn(previous.path);
}
export function restoreSuspendedEditor(container, session, options) {
  if (suspended !== session) return false;
  suspended = null;
  active = session;
  container.replaceChildren(session.element);
  session.element.inert = false;
  session.controller.resume(options);
  clearEditorReturn(session.path);
  return true;
}
const freshDrafts = new Set();
export const rememberEditorDraft = guid => freshDrafts.add(guid);
export const takeEditorDraft = guid => freshDrafts.delete(guid);
export const getEditorSession = () => active;
export function setEditorSession(session) { active = session; }
export function clearEditorSession() {
  const previous = active;
  active = null;
  previous?.controller.destroy();
}

// A pre-render boundary, unlike the asynchronous ROUTE_NAV_BEFORE notification.
export function allowEditorNavigation(path, { forceLeave = false } = {}) {
  if (!active) return true;
  const target = parseEditorPath(path);
  if (active.controller.isSaving()) return false;
  if (!forceLeave && target?.guid === active.guid) return true;
  if (!active.controller.canLeave()) return false;
  clearEditorSession();
  return true;
}
export function installEditorNavigationGuard(router) {
  router.hooks({ before(done, match) {
    if (allowEditorNavigation(match.url)) return done();
    // Back/Forward may have changed the hash already. Restore without remounting.
    if (active) history.replaceState(history.state, '', `#${active.path}`);
    done(false);
  } });
}

const RETURN_KEY = 'm5m_editor_return';
export function rememberEditorReturn(path) {
  if (!editorWorkspaceEnabled() || !(parseEditorPath(path) || String(path).replace(/^#/, '') === '/magnets/new')) return;
  try { sessionStorage.setItem(RETURN_KEY, JSON.stringify({ path: String(path).replace(/^#/, ''), at: Date.now() })); } catch { /* storage unavailable */ }
}
export function takeEditorReturn() {
  // A held working copy can outlive the short-lived bookmark handoff.
  if (suspended && editorWorkspaceEnabled()) { clearEditorReturn(suspended.path); return suspended.path; }
  try {
    const value = JSON.parse(sessionStorage.getItem(RETURN_KEY) || 'null');
    sessionStorage.removeItem(RETURN_KEY);
    return editorWorkspaceEnabled() && (parseEditorPath(value?.path) || value?.path === '/magnets/new') && Date.now() - value.at < 30 * 60 * 1000 ? value.path : null;
  } catch { return null; }
}
export function clearEditorReturn(path) {
  try {
    const value = JSON.parse(sessionStorage.getItem(RETURN_KEY) || 'null');
    if (value?.path === String(path).replace(/^#/, '')) sessionStorage.removeItem(RETURN_KEY);
  } catch { /* storage unavailable */ }
}
